// frontend/src/utils/continuationPlayback.js
//
// ⚠️ Placé dans `utils/` et non `lib/` : `.gitignore:17` ignore tout dossier
// `lib/` — règle générale pour les dossiers de build et de dépendances. Un
// fichier source y serait silencieusement absent du dépôt.
//
// Lecture CONTINUE d'une séquence `/animate`, sans couture.
//
// Voir docs/SPEC_CONTINUATION.md et docs/DOCTRINE_NON_REGRESSION_VISUELLE.md.
//
// ═══════════════════════════════════════════════════════════════════════════
// LE PROBLÈME
// ═══════════════════════════════════════════════════════════════════════════
//
// `/animate` sert une séquence FINIE. Le front la boucle, donc à la dernière
// frame la scène se téléporte vers la première. Mesuré sur les huit modules
// avant correction : de 29× à 205× le pas typique d'une frame.
//
// Le backend sait désormais CONTINUER une séquence : il renvoie un jeton, et
// l'appel suivant qui le présente reprend là où le précédent s'est arrêté.
// Ce module est la moitié cliente de ce contrat.
//
// ═══════════════════════════════════════════════════════════════════════════
// CE QUE CE MODULE N'EST PAS
// ═══════════════════════════════════════════════════════════════════════════
//
// Il ne calcule AUCUN observable, ne fabrique AUCUNE vitesse, n'invente AUCUNE
// position. Il livre des frames ; leur interprétation appartient au module, qui
// garde ses règles propres — appariement par identifiant pour la rivière de
// tokens, champ séparé pour les particules de la centrifugeuse.
//
// Il n'interpole JAMAIS entre deux frames non contiguës. La continuité vient du
// backend continué, jamais d'un maquillage ici (SPEC_CONTINUATION § 6.1).
//
// ⚠️ ENTRE UN MICRO-GEL ET UNE TÉLÉPORTATION, ON CHOISIT LE GEL.
// Si le segment suivant n'est pas prêt à temps, on fige la dernière frame le
// temps qu'il arrive. Reboucler sur le début est la régression interdite —
// c'est précisément le défaut que ce module existe pour supprimer.

/** Fraction du segment au-delà de laquelle on précharge le suivant. */
const SEUIL_PRECHARGEMENT = 0.75;

/** Une seule tentative de reprise après échec, puis séquence neuve. */
const TENTATIVES_AVANT_ABANDON = 1;

/**
 * Crée un lecteur continu.
 *
 * @param {object}   options
 * @param {string}   options.url            chemin de `/animate`, sans paramètres
 * @param {function} options.parametres     () => objet des paramètres du module
 * @param {function} [options.onSegment]    appelé à chaque nouveau segment reçu
 * @param {function} [options.onEtat]       appelé aux changements d'état
 */
export function creerLecteurContinu({ url, parametres, onSegment, onEtat }) {
  let segmentCourant = null;      // { frames, dt, jeton }
  let segmentSuivant = null;      // idem, préchargé
  let prechargementEnCours = false;
  let empreinteCourante = null;
  let bascules = 0;
  let replis = 0;                 // segments neufs servis faute de reprise
  let gels = 0;                   // bascules retardées, segment pas prêt
  let etat = "inactif";
  let abandon = new AbortController();
  let vivant = true;

  const empreinte = (p) =>
    JSON.stringify(Object.keys(p).sort().map((k) => [k, p[k]]));

  const changerEtat = (nouveau) => {
    if (etat === nouveau) return;
    etat = nouveau;
    if (onEtat) onEtat(etat);
  };

  /**
   * Demande un segment. `jeton` absent ⇒ séquence neuve.
   *
   * Un échec de reprise n'est pas une erreur : la route sert alors une
   * séquence neuve (SPEC_CONTINUATION § 3.1). On ne distingue donc pas les
   * deux cas ici — on compte seulement les replis, pour le diagnostic.
   */
  const demander = async (jeton, p) => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(p).map(([k, v]) => [k, String(v)]))
    );
    if (jeton) params.set("resume", jeton);

    const reponse = await fetch(`${url}?${params}`, { signal: abandon.signal });
    if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
    const data = await reponse.json();
    if (!Array.isArray(data.frames) || data.frames.length === 0) {
      throw new Error("séquence vide");
    }
    if (jeton && !data.continuation) {
      // Défaut de continuation, pas un cas normal : le backend devrait
      // toujours rendre un jeton. On repart proprement, en le signalant.
      replis += 1;
    }
    return { frames: data.frames, dt: data.dt || 0.02, jeton: data.continuation };
  };

  /** Premier segment, ou reprise complète après changement de paramètres. */
  const amorcer = async () => {
    const p = parametres();
    empreinteCourante = empreinte(p);
    changerEtat("chargement");
    try {
      const segment = await demander(null, p);
      if (!vivant || empreinteCourante !== empreinte(parametres())) return;
      segmentCourant = segment;
      segmentSuivant = null;
      if (onSegment) onSegment(segment);
      changerEtat("lecture");
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(`Lecteur continu (${url}) — amorçage :`, err);
        changerEtat("erreur");
      }
    }
  };

  /** Précharge le segment suivant à partir du jeton courant. */
  const precharger = async () => {
    if (prechargementEnCours || segmentSuivant || !segmentCourant) return;
    prechargementEnCours = true;

    const p = parametres();
    const marque = empreinte(p);
    try {
      const segment = await demander(segmentCourant.jeton, p);
      // Les paramètres ont pu changer pendant la requête : on jette plutôt que
      // de servir un segment produit avec d'autres réglages.
      if (vivant && marque === empreinteCourante) segmentSuivant = segment;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.warn(`Lecteur continu (${url}) — préchargement :`, err);
      }
    } finally {
      prechargementEnCours = false;
    }
  };

  return {
    /**
     * À appeler à chaque frame de rendu, avec le temps écoulé.
     *
     * Renvoie `{ a, b, alpha, dt }` — deux frames CONTIGUËS et le facteur
     * d'interpolation entre elles — ou `null` si rien n'est prêt.
     *
     * ⚠️ `a` et `b` sont toujours consécutives DANS LE MÊME segment. Le
     * lecteur ne fabrique jamais de transition entre deux segments : la
     * bascule a lieu à la frontière, où les états sont déjà contigus parce
     * que le backend a continué la simulation.
     */
    lire(playhead) {
      if (!segmentCourant) return null;

      const { frames, dt } = segmentCourant;
      const position = playhead / dt;
      const ia = Math.floor(position);

      // Préchargement anticipé : jamais à la dernière frame.
      if (ia >= frames.length * SEUIL_PRECHARGEMENT) precharger();

      if (ia < frames.length - 1) {
        return {
          a: frames[ia],
          b: frames[ia + 1],
          alpha: position - ia,
          dt,
          reste: (frames.length - 1 - ia) * dt,
        };
      }

      // Fin du segment.
      if (segmentSuivant) {
        segmentCourant = segmentSuivant;   // l'ancien est libéré ici
        segmentSuivant = null;
        bascules += 1;
        if (onSegment) onSegment(segmentCourant);
        changerEtat("lecture");
        const s = segmentCourant;
        return { a: s.frames[0], b: s.frames[1] || s.frames[0], alpha: 0,
                 dt: s.dt, reste: (s.frames.length - 1) * s.dt, bascule: true };
      }

      // Pas prêt : on GÈLE la dernière frame. On ne reboucle pas.
      gels += 1;
      changerEtat("attente");
      precharger();
      const derniere = frames[frames.length - 1];
      return { a: derniere, b: derniere, alpha: 0, dt, reste: 0, gel: true };
    },

    /**
     * Signale un changement de paramètres. Si l'empreinte a bougé, la
     * continuation est INVALIDÉE et une séquence neuve démarre : un jeton émis
     * pour d'autres réglages ne doit jamais être réutilisé.
     *
     * Renvoie `true` si une reprise a eu lieu — l'appelant remet son playhead
     * à zéro.
     */
    verifierParametres() {
      if (!segmentCourant) return false;
      if (empreinte(parametres()) === empreinteCourante) return false;
      abandon.abort();
      abandon = new AbortController();
      segmentSuivant = null;
      prechargementEnCours = false;
      amorcer();
      return true;
    },

    demarrer() {
      vivant = true;
      amorcer();
    },

    arreter() {
      vivant = false;
      abandon.abort();
      segmentCourant = null;
      segmentSuivant = null;
    },

    /** Diagnostic — pour identifier une couture persistante sans redeviner. */
    diagnostic() {
      return {
        etat,
        jeton: segmentCourant ? Boolean(segmentCourant.jeton) : false,
        suivantPret: Boolean(segmentSuivant),
        prechargement: prechargementEnCours,
        bascules,
        gels,
        replis,
      };
    },
  };
}
